/**
 * Custom Hook: useExerciseModal
 *
 * Responsabilidade:
 * - Gerenciar todo o estado do Modal de Exercícios (formulário, previews, loading).
 * - Orquestrar efeitos colaterais (useEffect).
 * - Expor métodos para manipulação da UI e submissão de dados.
 * - Integrar lógica de negócios e utilitários.
 */

import { useState, useEffect, FormEvent, ChangeEvent } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import Strings from "../../constants/Strings";
import utils from "../../helpers/utils";
import {
  ExerciseModalLogicProps,
  ExerciseFormState,
} from "./exerciseModalTypes";
import { saveExercise } from "./exerciseModalLogic";
import exercicies from "../../services/exercicies";
import {
  validateImage,
  validateVideo,
  readFileAsBase64,
} from "./exerciseModalUtils";
import {
  completeVideoUpload,
  getVideoAsset,
  initVideoUpload,
  presignVideoPart,
} from "../../services/videos";

export type VideoProcessingStatus =
  | "idle"
  | "uploading"
  | "processing"
  | "ready"
  | "failed";

function extractAssetIdFromPlaybackUrl(playbackUrl: string): string | null {
  const match = playbackUrl.match(/\/hls\/([^/]+)\/master\.m3u8/);
  return match?.[1] ?? null;
}

export const useExerciseModal = ({
  isOpen,
  onBack,
  data,
  onSuccess = () => {},
  allowCatalogEdit = false,
  approveOnSave = false,
}: ExerciseModalLogicProps) => {
  const { getUser } = useAuth();
  const user = getUser();
  const professional_id = user?.id;

  const [form, setForm] = useState<ExerciseFormState>({
    name: "",
    image: "",
    professional_id: professional_id ? Number(professional_id) : undefined,
    video: "",
    description: "",
    id: undefined,
  });

  const [imagePreview, setImagePreview] = useState<string>("");
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [videoSectionExpanded, setVideoSectionExpanded] = useState(true);
  const [imageSectionExpanded, setImageSectionExpanded] = useState(true);
  const [videoProcessingStatus, setVideoProcessingStatus] =
    useState<VideoProcessingStatus>("idle");
  const [videoAssetId, setVideoAssetId] = useState<string | null>(null);

  const canEditCatalogExercise = Boolean(
    user?.is_admin || (allowCatalogEdit && user?.is_staff),
  );

  const isReadOnly =
    form.id !== undefined &&
    form.id !== null &&
    (form.all_view === true || data?.all_view === true) &&
    !canEditCatalogExercise;

  useEffect(() => {
    if (isOpen && data) {
      setForm((prevForm) => ({
        ...prevForm,
        id: undefined,
        professional_id: professional_id ? Number(professional_id) : undefined,
        ...data,
      }));
      setImagePreview(
        data.image_url ||
          utils.getUriImage(
            data.image ?? Strings.default_photo,
            data.all_view ? "all_view" : String(data.professional_id ?? "all_view"),
          ),
      );

      if (data.video?.startsWith("data:")) {
        setVideoPreview(data.video);
      } else if (data.video) {
        setVideoPreview(
          utils.getUriImage(
            data.video,
            data.professional_id ? String(data.professional_id) : "all_view",
          ),
        );
      } else {
        setVideoPreview("");
      }

      setVideoSectionExpanded(true);
      setImageSectionExpanded(true);

      // Verifica se vídeo existente está em processamento (HLS)
      if (data.video?.includes(".m3u8")) {
        const assetId = extractAssetIdFromPlaybackUrl(data.video);
        if (assetId) {
          setVideoAssetId(assetId);
          setVideoProcessingStatus("processing");
        } else {
          setVideoAssetId(null);
          setVideoProcessingStatus("ready");
        }
      } else {
        setVideoAssetId(null);
        setVideoProcessingStatus(data.video ? "ready" : "idle");
      }
    } else if (!isOpen) {
      setVideoAssetId(null);
      setVideoProcessingStatus("idle");
    }
  }, [isOpen, data, professional_id]);

  // Polling do status do vídeo enquanto processa
  useEffect(() => {
    if (!videoAssetId || !isOpen) return;

    const statusesToPoll: VideoProcessingStatus[] = ["processing", "uploading"];
    if (!statusesToPoll.includes(videoProcessingStatus)) return;

    const poll = async () => {
      try {
        const result = await getVideoAsset(videoAssetId);
        const status = result.status?.toUpperCase?.() ?? "";
        if (status === "READY") {
          setVideoProcessingStatus("ready");
          return;
        }
        if (status === "FAILED") {
          setVideoProcessingStatus("failed");
          toast.error(
            "Falha ao processar o vídeo. Tente fazer upload novamente.",
          );
          return;
        }
        setVideoProcessingStatus(
          status === "UPLOADING" ? "uploading" : "processing",
        );
      } catch {
        // Ignora erros de polling, tenta novamente no próximo ciclo
      }
    };

    const interval = setInterval(poll, 5000);
    poll();

    return () => clearInterval(interval);
  }, [videoAssetId, videoProcessingStatus, isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isReadOnly) {
      toast.error("Este exercício é público e não pode ser alterado.");
      return;
    }

    setLoading(true);

    const result = await saveExercise(form, professional_id);

    if (result.ok && approveOnSave && form.id) {
      const approval = await exercicies.updateFields(String(form.id), {
        classification: "yes",
      });
      if (!approval.ok) {
        setLoading(false);
        toast.error(
          approval.error ??
            "Exercício salvo, mas não foi possível aprová-lo na curadoria.",
        );
        return;
      }
    }

    setLoading(false);

    if (result.ok) {
      const isEdit =
        form.id !== undefined && form.id !== null && form.id !== 0;
      toast.success(
        approveOnSave && isEdit
          ? "Exercício atualizado e aprovado."
          : isEdit
            ? "Exercício atualizado com sucesso!"
            : "Exercício criado com sucesso!",
      );
      onSuccess();
      onBack();
    } else {
      toast.error(result.error ?? "Erro ao salvar o exercício.");
    }
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateImage(file)) return;

      try {
        const base64String = await readFileAsBase64(file);
        setForm({ ...form, image: base64String });
        setImagePreview(base64String);
        setImageSectionExpanded(true);
      } catch (error) {
        console.error("Error reading image file", error);
        toast.error("Erro ao processar a imagem.");
      }
    }
  };

  const handleVideoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateVideo(file)) return;

      try {
        setLoading(true);

        // Preview imediato local (MP4) enquanto o HLS processa
        const localUrl = URL.createObjectURL(file);
        setVideoPreview(localUrl);
        setVideoSectionExpanded(true);

        const init = await initVideoUpload({
          fileName: file.name,
          contentType: file.type || "video/mp4",
        });

        const partSize = init.partSize || 10 * 1024 * 1024;
        const totalParts = Math.ceil(file.size / partSize);

        const uploadedParts: Array<{ ETag: string; PartNumber: number }> = [];
        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, file.size);
          const blobPart = file.slice(start, end);

          const { url } = await presignVideoPart({
            assetId: init.assetId,
            partNumber,
          });

          const resp = await fetch(url, { method: "PUT", body: blobPart });
          if (!resp.ok) {
            throw new Error(
              `Falha ao enviar parte ${partNumber}/${totalParts}`,
            );
          }

          const etag = resp.headers.get("ETag");
          if (!etag) {
            throw new Error(
              "Upload concluído, mas não foi possível ler o ETag (verifique CORS expose_headers no bucket).",
            );
          }
          uploadedParts.push({ ETag: etag, PartNumber: partNumber });
        }

        const completed = await completeVideoUpload({
          assetId: init.assetId,
          parts: uploadedParts,
        });

        setForm((prev) => ({ ...prev, video: completed.playbackUrl }));
        setVideoAssetId(completed.assetId);
        const status = completed.status?.toUpperCase();
        setVideoProcessingStatus(status === "READY" ? "ready" : "processing");
        toast.info(
          "Upload concluído. O vídeo está sendo processado e pode levar alguns minutos.",
        );
      } catch (error) {
        console.error("Error uploading video file", error);
        toast.error("Erro ao enviar/processar o vídeo.");
      } finally {
        setLoading(false);
      }
    }
  };

  const clearImage = () => {
    setForm({ ...form, image: "" });
    setImagePreview("");
  };

  const clearVideo = () => {
    setForm((prev) => ({ ...prev, video: "" }));
    setVideoPreview("");
    setVideoAssetId(null);
    setVideoProcessingStatus("idle");
  };

  const toggleVideoSection = () => setVideoSectionExpanded((prev) => !prev);
  const toggleImageSection = () => setImageSectionExpanded((prev) => !prev);

  const updateField = <K extends keyof ExerciseFormState>(
    field: K,
    value: ExerciseFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateMuscleGroups = (groups: string[]) => {
    setForm((prev) => ({
      ...prev,
      muscle_groups: groups,
      area_muscular: groups,
    }));
  };

  return {
    form,
    imagePreview,
    videoPreview,
    loading,
    videoProcessingStatus,
    videoSectionExpanded,
    imageSectionExpanded,
    isReadOnly,
    submit: handleSubmit,
    handleImageChange,
    handleVideoChange,
    clearImage,
    clearVideo,
    toggleVideoSection,
    toggleImageSection,
    updateField,
    updateMuscleGroups,
  };
};
